/// <reference path="../../../types/learnosity.d.ts" />

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction
} from "react";

export interface CurriculumConnection {
  curriculum_code: string;
  term_focus: string;
  lesson_focus: string;
  curriculum_description: string;
  deep_links: DeepLink[];
}

export interface DeepLink {
  subtopic_title: string;
  deep_link: string;
  id: number;
  service: string;
}

export interface TermBasedMessage {
  type: string;
  look_and_feel: null | string;
  message: string;
}

export interface Material {
  name: string;
  image: string;
  adult_supervision: boolean;
  alternatives?: string;
  description?: string;
  type: string;
  category: string;
  use: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface LearnosityRequest {
  activity_id?: string;
  name?: string;
  state?: "initial" | "resumed" | "review";
  rendering_type?: "assess" | "inline";
  type?: "submit_practice" | "local_practice" | "preview";
  session_id?: string;
  user_id?: string;
  config?: Record<string, any>;
  items?: string[];
  security?: {
    consumer_key: string;
    domain: string;
    timestamp: string;
    user_id: string;
    signature: string;
  };
  [key: string]: any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Lesson-specific data grouped for the learning MFE. */
export interface LessonData {
  curriculum?: CurriculumConnection | null;
  learningObjectives?: string[];
  materials?: Material[];
  termBasedMessaging?: TermBasedMessage[];
  status?: string;
  coverImageUrl?: string;
  coverImageAltText?: string;
  /**
   * BLD2-458: Per-grade Read Aloud rollout flag, baked into the
   * lesson_catalogue Meilisearch doc by Portal at sync time. Single
   * source of truth for whether to render the new Polly AudioPlayer.
   * Defaults to `false` if the field is missing from the lesson payload.
   */
  audioEnabled?: boolean;
  /**
   * BLD2-458 V2: Per-section relative CDN paths for pre-generated Read
   * Aloud audio. Populated by Portal -> Meilisearch -> OEX without an
   * extra status round-trip. Mirrors `coverImageUrl` (also a relative
   * path) — MFE builds the full URL using `VITE_CDN_BASE_URL`.
   *
   * Shape: `{ learn: "lessons/audio/280/learn/Olivia.mp3", textbook: "..." }`
   * Empty/missing when audio hasn't been generated for that section yet.
   */
  audioPaths?: Record<string, string>;
  /**
   * BLD2-463: Per-student Read Aloud notification banner visibility flag.
   * Sourced from user.profile.meta via the lesson API (BLD2-462). Defaults
   * to `true` (show banner). False once the student permanently dismisses
   * via the X button. Absent before BLD2-462 is deployed — treat as `true`.
   */
  audioBannerVisible?: boolean;
  applyActivityItems?: Record<string, string>;
  reflectionActivityItems?: Record<string, string>;
  learnActivityItems?: { learn: string };
  textbookActivityItems?: { textbook: string };
}

/** Placeholder for portal-specific data. */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface PortalData {
  applyActivityItems?: Record<string, string>;
}

/** Data passed to `initialize()` to set content and start the SDK. */
export interface InitializeData {
  learnosityItems: string[];
  activityRequest: LearnosityRequest;
  lessonData?: LessonData;
  portalData?: PortalData;
}

export interface LearnosityContextValue {
  // Player state
  isLearnosityReady: boolean;
  isLessonsSectionLoading: boolean;
  learnosityError: string | null;
  itemsAppRef: MutableRefObject<ItemsApp | null>;
  setIsLessonsSectionLoading: Dispatch<SetStateAction<boolean>>;
  setLearnosityError: Dispatch<SetStateAction<string | null>>;
  setIsLearnosityReady: Dispatch<SetStateAction<boolean>>;

  // Content
  learnosityItems: string[];
  lessonData: LessonData;
  portalData?: PortalData;

  // Initialization API
  initialize: (data: InitializeData) => void;
  reset: () => void;

}

export const LearnosityContext = createContext<LearnosityContextValue | null>(
  null
);

const MSG_ERROR = "We couldn't load the activity. Please try again.";

export const LearnosityProvider = ({ children }: { children: ReactNode }) => {
  // Player state
  const [isLearnosityReady, setIsLearnosityReady] = useState(false);
  const [isLessonsSectionLoading, setIsLessonsSectionLoading] = useState(true);
  const [learnosityError, setLearnosityError] = useState<string | null>(null);
  const itemsAppRef = useRef<ItemsApp | null>(null);

  // Content state
  const [content, setContent] = useState<InitializeData | null>(null);

  // Internal refs for attempt tracking / cancellation
  const attemptIdRef = useRef(0);
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInitTimeout = () => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
  };

  const resetPlayer = useCallback(() => {
    clearInitTimeout();
    if (itemsAppRef.current) {
      setIsLearnosityReady(false);
      try {
        itemsAppRef.current.reset();
      } catch (e) {
        console.warn(
          "[Learnosity Player] Error resetting previous instance",
          e
        );
      }
      itemsAppRef.current = null;
    }
  }, []);

  const initialize = useCallback(
    (data: InitializeData) => {
      resetPlayer();
      setLearnosityError(null);
      setContent(data);
      attemptIdRef.current += 1;
    },
    [resetPlayer]
  );

  const reset = useCallback(() => {
    resetPlayer();
    setContent(null);
    setLearnosityError(null);
    setIsLessonsSectionLoading(true);
    attemptIdRef.current += 1;
  }, [resetPlayer]);

  // When content is set, initialise the Learnosity SDK
  useEffect(() => {
    if (!content) return;

    const attemptId = attemptIdRef.current;

    // Guard: check both the object AND the init method exist
    if (!window.LearnosityItems || typeof window.LearnosityItems.init !== 'function') {
      console.error('[Learnosity Player] SDK not loaded or init not available', {
        LearnosityItems: typeof window.LearnosityItems,
        init: typeof window.LearnosityItems?.init,
      });
      setLearnosityError(MSG_ERROR);
      return;
    }

    // Hard timeout
    initTimeoutRef.current = setTimeout(() => {
      if (attemptIdRef.current !== attemptId) return;
      setIsLearnosityReady(false);
      setLearnosityError(MSG_ERROR);
    }, 10_000);

    // Track the in-flight items app so cleanup can cancel it if content changes
    // before readyListener fires (e.g. user clicks "Begin lesson" while intro
    // Learnosity is still initialising). Without this, resetPlayer() has no
    // reference to cancel and two concurrent LearnosityItems.init() calls race,
    // producing 50001: DOM element missing. See: PLAN-310.
    let inflightItemsApp: ItemsApp | null = null;

    try {
      const itemsApp = window.LearnosityItems.init(content.activityRequest, {
        readyListener() {
          if (attemptIdRef.current !== attemptId) return;
          clearInitTimeout();
          setIsLearnosityReady(true);
          setLearnosityError(null);
          itemsAppRef.current = itemsApp;
          window.itemsApp = itemsApp;
          inflightItemsApp = null; // successfully claimed — do not reset in cleanup
        },
        errorListener(err) {
          if (attemptIdRef.current !== attemptId) return;
          clearInitTimeout();
          console.error("[Learnosity Player] init error", err);
          setIsLearnosityReady(false);
          setLearnosityError(err?.msg || err?.message || MSG_ERROR);
          inflightItemsApp = null;
        }
      });
      // Store immediately (before readyListener) so the cleanup below can cancel it
      inflightItemsApp = itemsApp;
    } catch (err) {
      console.error('[Learnosity Player] init threw exception', err);
      clearInitTimeout();
      setLearnosityError(MSG_ERROR);
    }

    return () => {
      clearInitTimeout();
      // Cancel any in-flight init that hasn't fired readyListener yet.
      // This is the path taken when content changes (section navigation)
      // before the SDK has finished initialising — prevents stale inits
      // from interfering with the next section's initialisation.
      if (inflightItemsApp) {
        try {
          inflightItemsApp.reset();
        } catch (e) {
          console.warn('[Learnosity Player] error cancelling in-flight init', e);
        }
        inflightItemsApp = null;
      }
    };
  }, [content]);

  // Keep isLessonsSectionLoading in sync with isLearnosityReady
  useEffect(() => {
    setIsLessonsSectionLoading(!isLearnosityReady);
  }, [isLearnosityReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetPlayer();
    };
  }, []);

  const value: LearnosityContextValue = {
    isLearnosityReady,
    isLessonsSectionLoading,
    learnosityError,
    itemsAppRef,
    learnosityItems: content?.learnosityItems ?? [],
    lessonData: content?.lessonData ?? {},
    portalData: content?.portalData,
    setLearnosityError,
    setIsLessonsSectionLoading,
    setIsLearnosityReady,
    initialize,
    reset
  };

  return (
    <LearnosityContext.Provider value={value}>
      {children}
    </LearnosityContext.Provider>
  );
};
